import { toast, ToastContainer } from 'react-toastify';
import { Container } from "react-bootstrap";
import {useState, useEffect} from 'react';
import {BreathAPIs} from '../../APIs/BreathAPIs';
import { Table, Form, Input, Select, DatePicker, Button, Modal, Popconfirm, Tag, Space } from 'antd';
import moment from 'moment';

const SOFTWARE_TYPES = [
    { label: 'Web App', value: 'webApp' },
    { label: 'Native App (Mobile)', value: 'mobileApp' },
    { label: 'Breathe BE', value: 'breatheBE' },
    { label: 'CSMS', value: 'csms' },
];

const SoftwareVersion = () => {
    const [versionData, setVersionData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [pagination, setPagination] = useState({
        total: 0,
        current: 1,
        pageSize: 5,
        totalPages: 0,
    });
    const [filterSoftwareType, setFilterSoftwareType] = useState(null);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingRecord, setEditingRecord] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [form] = Form.useForm();

    const fetchVersion = async (page = pagination.current, pageSize = pagination.pageSize, softwareType = filterSoftwareType) => {
        try{
            setIsLoading(true);
            let softwareVersionHistory = await BreathAPIs.getSoftwareVersionList(page, pageSize, softwareType);
            setVersionData(softwareVersionHistory.data.softwareVersionList);
            setPagination({
                total: softwareVersionHistory.data.pagination.total,
                current: softwareVersionHistory.data.pagination.current,
                pageSize: softwareVersionHistory.data.pagination.pageSize,
                totalPages: softwareVersionHistory.data.pagination.totalPages
            });
        }catch(error){
            console.error("Error fetching version data:", error);
            toast.error("Error fetching version data");
            setVersionData([]);
        }finally{
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchVersion();
    }, []);

    const handleTableChange = (paginationConfig) => {
        fetchVersion(paginationConfig.current, paginationConfig.pageSize, filterSoftwareType);
    };

    const handleFilterChange = (softwareType) => {
        setFilterSoftwareType(softwareType);
        fetchVersion(1, pagination.pageSize, softwareType);
    };

    const showAddModal = () => {
        setEditingRecord(null);
        form.resetFields();
        setIsModalVisible(true);
    };

    const showEditModal = (record) => {
        setEditingRecord(record);
        form.setFieldsValue({
            software: record.software,
            softwareVersion: record.softwareVersion,
            releaseDate: record.releaseDate ? moment(record.releaseDate) : null,
            features: record.features || [],
            description: record.description,
        });
        setIsModalVisible(true);
    };

    const handleModalCancel = () => {
        setIsModalVisible(false);
        setEditingRecord(null);
        form.resetFields();
    };

    const handleSubmit = async () => {
        try{
            const values = await form.validateFields();
            const payload = {
                ...values,
                releaseDate: values.releaseDate ? values.releaseDate.toISOString() : null,
            };
            setIsSubmitting(true);
            if(editingRecord){
                await BreathAPIs.updateSoftwareVersion(editingRecord._id, payload);
                toast.success("Software version updated successfully");
            }else{
                await BreathAPIs.addSoftwareVersion(payload);
                toast.success("Software version added successfully");
            }
            setIsModalVisible(false);
            setEditingRecord(null);
            form.resetFields();
            fetchVersion(editingRecord ? pagination.current : 1, pagination.pageSize, filterSoftwareType);
        }catch(error){
            if(error?.errorFields){
                return;
            }
            console.error("Error saving software version:", error);
            toast.error(editingRecord ? "Error updating software version" : "Error adding software version");
        }finally{
            setIsSubmitting(false);
        }
    };

    const handleDelete = async (record) => {
        try{
            await BreathAPIs.deleteSoftwareVersion(record._id);
            toast.success("Software version deleted successfully");
            fetchVersion();
        }catch(error){
            console.error("Error deleting software version:", error);
            toast.error("Error deleting software version");
        }
    };

    const columns = [
        {
            title: 'Software',
            dataIndex: 'software',
            key: 'software',
            render: (software) => SOFTWARE_TYPES.find(t => t.value === software)?.label || software,
        },
        {
            title: 'Version',
            dataIndex: 'softwareVersion',
            key: 'softwareVersion',
        },
        {
            title: 'Release Date',
            dataIndex: 'releaseDate',
            key: 'releaseDate',
            render: (releaseDate) => releaseDate ? moment(releaseDate).format('YYYY-MM-DD') : 'N/A',
        },
        {
            title: 'Features',
            dataIndex: 'features',
            key: 'features',
            render: (features) => (features && features.length > 0) ? features.map((f, i) => <Tag key={i}>{f}</Tag>) : 'N/A',
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button size="small" onClick={() => showEditModal(record)}>Edit</Button>
                    <Popconfirm
                        title="Delete this software version?"
                        onConfirm={() => handleDelete(record)}
                        okText="Delete"
                        cancelText="Cancel"
                    >
                        <Button size="small" danger>Delete</Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <Container>
            <ToastContainer />
            <div className="d-flex justify-content-between align-items-center mb-3 mt-3">
                <Select
                    allowClear
                    placeholder="Filter by software"
                    style={{ width: 220 }}
                    options={SOFTWARE_TYPES}
                    value={filterSoftwareType}
                    onChange={handleFilterChange}
                />
                <Button type="primary" onClick={showAddModal}>Add Software Version</Button>
            </div>
            <Table
                loading={isLoading}
                dataSource={versionData}
                columns={columns}
                rowKey="_id"
                pagination={pagination}
                onChange={handleTableChange}
                bordered
                size="middle"
            />
            <Modal
                title={editingRecord ? "Edit Software Version" : "Add Software Version"}
                open={isModalVisible}
                onOk={handleSubmit}
                onCancel={handleModalCancel}
                confirmLoading={isSubmitting}
                okText={editingRecord ? "Update" : "Add"}
            >
                <Form form={form} layout="vertical">
                    <Form.Item name="software" label="Software" rules={[{ required: true, message: 'Please select a software type' }]}>
                        <Select options={SOFTWARE_TYPES} placeholder="Select software type" />
                    </Form.Item>
                    <Form.Item name="softwareVersion" label="Version" rules={[{ required: true, message: 'Please enter a version, e.g. v1.0.0' }]}>
                        <Input placeholder="v1.0.0" />
                    </Form.Item>
                    <Form.Item name="releaseDate" label="Release Date" rules={[{ required: true, message: 'Please select a release date' }]}>
                        <DatePicker style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item name="features" label="Features">
                        <Select mode="tags" placeholder="Add features and press enter" />
                    </Form.Item>
                    <Form.Item name="description" label="Description">
                        <Input.TextArea rows={3} />
                    </Form.Item>
                </Form>
            </Modal>
        </Container>
    )
}

export default SoftwareVersion
